/**
 * PhotoDetailModal - Full-screen photo viewer with metadata panel + tag management.
 * Left: full image with tag markers. Right: metadata panel (editable) + references list.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ActiveViewers } from '../common/ViewingTemplate';

const formatDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const TAG_TYPES = [
  { value: 'RFI', label: 'RFI' },
  { value: 'ISO', label: 'ISO Sheet' },
  { value: 'TRANSMITTAL', label: 'Transmittal' },
  { value: 'TRACKER_PIPING', label: 'Piping Tracker' },
  { value: 'TRACKER_MECHANICAL', label: 'Mechanical Tracker' },
  { value: 'TRACKER_STRUCTURAL', label: 'Structural Tracker' },
  { value: 'DAILY_REPORT', label: 'Daily Report' },
  { value: 'QA_QC', label: 'QA/QC' },
  { value: 'OTHER', label: 'Other' },
];

const PhotoDetailModal = ({ photo, projectId, onClose, onDelete, onUpdated, viewers, isConnected, connectionState }) => {
  const { instance, accounts } = useMsal();
  const [tags, setTags] = useState(photo.tags || []);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const imageRef = useRef(null);

  // Spatial tag creation (image click)
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagPos, setNewTagPos] = useState(null);
  const [tagForm, setTagForm] = useState({ linked_item_type: 'RFI', linked_item_id: '', description: '' });
  const [tagSaving, setTagSaving] = useState(false);

  // Non-spatial reference adding (panel button)
  const [isAddingRef, setIsAddingRef] = useState(false);
  const [refForm, setRefForm] = useState({ linked_item_type: 'RFI', linked_item_id: '', description: '' });
  const [refSaving, setRefSaving] = useState(false);

  // Metadata editing
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: photo.title || '',
    description: photo.description || '',
    category: photo.category || '',
    location: photo.location || '',
    date_taken: photo.date_taken ? new Date(photo.date_taken).toISOString().split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);

  // Hovered tag
  const [hoveredTag, setHoveredTag] = useState(null);

  // Load full-res image
  useEffect(() => {
    if (!photo.id) return;
    setImageLoading(true);
    authenticatedApiCall(instance, accounts, 'GET', `/technical-photos/photo/${photo.id}/file`, null, { responseType: 'blob' })
      .then(res => {
        if (res.data && res.data.size) {
          setImageUrl(URL.createObjectURL(res.data));
        }
      })
      .catch(err => console.error('Photo load error:', err))
      .finally(() => setImageLoading(false));

    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [photo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageClick = (e) => {
    if (isAddingTag) return;
    const rect = e.target.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setNewTagPos({ x, y });
    setIsAddingTag(true);
    setTagForm({ linked_item_type: 'RFI', linked_item_id: '', description: '' });
  };

  const handleSaveTag = async () => {
    if (!tagForm.linked_item_id && !tagForm.description) return;
    setTagSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/technical-photos/${photo.id}/tags`, {
        x_coordinate: newTagPos.x,
        y_coordinate: newTagPos.y,
        ...tagForm,
      });
      if (res.data?.success) {
        setTags(prev => [...prev, res.data.data]);
        setIsAddingTag(false);
        setNewTagPos(null);
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Tag save error:', err);
    } finally {
      setTagSaving(false);
    }
  };

  const handleSaveRef = async () => {
    if (!refForm.linked_item_id && !refForm.description) return;
    setRefSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `/technical-photos/${photo.id}/tags`, {
        x_coordinate: -1,
        y_coordinate: -1,
        ...refForm,
      });
      if (res.data?.success) {
        setTags(prev => [...prev, res.data.data]);
        setIsAddingRef(false);
        setRefForm({ linked_item_type: 'RFI', linked_item_id: '', description: '' });
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Reference save error:', err);
    } finally {
      setRefSaving(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/technical-photos/tags/${tagId}`);
      setTags(prev => prev.filter(t => t.id !== tagId));
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Tag delete error:', err);
    }
  };

  const handleSaveMetadata = async () => {
    setSaving(true);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/technical-photos/photo/${photo.id}`, editForm);
      setIsEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Metadata save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (isAddingTag) {
        setIsAddingTag(false);
        setNewTagPos(null);
      } else {
        onClose();
      }
    }
  }, [isAddingTag, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const inputStyle = {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle = { display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '3px', fontWeight: 500 };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.92)',
      display: 'flex', flexDirection: 'column', zIndex: 9999,
    }}>
      {/* Top bar */}
      <div style={{
        padding: '10px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '15px', fontWeight: 600 }}>
            {photo.title || photo.file_name || 'Photo'}
          </h3>
          {photo.category && (
            <span style={{ padding: '2px 8px', backgroundColor: 'rgba(255,255,255,0.15)', color: '#d1d5db', borderRadius: '12px', fontSize: '11px' }}>
              {photo.category}
            </span>
          )}
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={3} showConnectionStatus={false} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onDelete}
            style={{ padding: '6px 12px', backgroundColor: 'rgba(220,38,38,0.8)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
          >
            Delete
          </button>
          <button
            onClick={onClose}
            style={{ padding: '6px 14px', backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
          >
            Close (Esc)
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Image area */}
        <div style={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
          {imageLoading ? (
            <div style={{ color: '#9ca3af', fontSize: '15px' }}>Loading image...</div>
          ) : imageUrl ? (
            <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }}>
              <img
                ref={imageRef}
                src={imageUrl}
                alt={photo.title || ''}
                onClick={handleImageClick}
                style={{
                  maxHeight: 'calc(100vh - 60px)',
                  maxWidth: '100%',
                  display: 'block',
                  cursor: isAddingTag ? 'default' : 'crosshair',
                  borderRadius: '2px',
                }}
              />

              {/* Spatial tags (pinned to image) — skip non-spatial refs with x=-1 */}
              {tags.filter(t => t.x_coordinate >= 0 && t.y_coordinate >= 0).map((tag, idx) => (
                <div
                  key={tag.id}
                  style={{
                    position: 'absolute',
                    left: `${tag.x_coordinate}%`,
                    top: `${tag.y_coordinate}%`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 5,
                  }}
                  onMouseEnter={() => setHoveredTag(tag.id)}
                  onMouseLeave={() => setHoveredTag(null)}
                >
                  <div style={{
                    width: 24, height: 24,
                    backgroundColor: hoveredTag === tag.id ? 'rgba(37,99,235,0.9)' : 'rgba(239,68,68,0.7)',
                    border: '2px solid white',
                    borderRadius: '50%',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', color: 'white', fontWeight: 'bold',
                  }}>
                    {idx + 1}
                  </div>
                  {/* Tooltip */}
                  {hoveredTag === tag.id && (
                    <div style={{
                      position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                      marginTop: '6px', padding: '6px 10px',
                      backgroundColor: 'rgba(0,0,0,0.85)', color: 'white',
                      borderRadius: '6px', fontSize: '12px', whiteSpace: 'nowrap',
                      pointerEvents: 'none', zIndex: 10,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    }}>
                      <strong>{tag.linked_item_type}</strong>: {tag.linked_item_id}
                      {tag.description && <div style={{ color: '#d1d5db', marginTop: '2px' }}>{tag.description}</div>}
                    </div>
                  )}
                </div>
              ))}

              {/* New tag marker */}
              {isAddingTag && newTagPos && (
                <div style={{
                  position: 'absolute',
                  left: `${newTagPos.x}%`,
                  top: `${newTagPos.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: 26, height: 26,
                  backgroundColor: 'rgba(34,197,94,0.8)',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  zIndex: 6,
                }} />
              )}
            </div>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '15px' }}>Failed to load image</div>
          )}

          {/* Click instruction */}
          {!isAddingTag && !imageLoading && imageUrl && (
            <div style={{
              position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
              padding: '5px 14px', backgroundColor: 'rgba(0,0,0,0.6)', color: '#d1d5db',
              borderRadius: '20px', fontSize: '11px', pointerEvents: 'none',
              backdropFilter: 'blur(4px)',
            }}>
              Click on the image to add a reference tag
            </div>
          )}
        </div>

        {/* Right panel */}
        <div style={{
          width: '320px', backgroundColor: '#ffffff', borderLeft: '1px solid #e5e7eb',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
        }}>
          {/* Tag creation form (shown when adding) */}
          {isAddingTag && (
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f0fdf4' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#15803d' }}>Add Reference Tag</h4>
              <div style={{ marginBottom: '8px' }}>
                <label style={labelStyle}>Reference Type</label>
                <select
                  value={tagForm.linked_item_type}
                  onChange={(e) => setTagForm(p => ({ ...p, linked_item_type: e.target.value }))}
                  style={inputStyle}
                >
                  {TAG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <label style={labelStyle}>ID / Number</label>
                <input
                  type="text"
                  value={tagForm.linked_item_id}
                  onChange={(e) => setTagForm(p => ({ ...p, linked_item_id: e.target.value }))}
                  placeholder="e.g. RFI-001 or ISO-1234"
                  style={inputStyle}
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={labelStyle}>Note (optional)</label>
                <textarea
                  value={tagForm.description}
                  onChange={(e) => setTagForm(p => ({ ...p, description: e.target.value }))}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '40px' }}
                  rows={2}
                  placeholder="Brief note about this reference"
                />
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={handleSaveTag}
                  disabled={tagSaving || (!tagForm.linked_item_id && !tagForm.description)}
                  style={{
                    flex: 1, padding: '7px', backgroundColor: '#16a34a', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                    opacity: (tagSaving || (!tagForm.linked_item_id && !tagForm.description)) ? 0.6 : 1,
                  }}
                >
                  {tagSaving ? 'Saving...' : 'Save Tag'}
                </button>
                <button
                  onClick={() => { setIsAddingTag(false); setNewTagPos(null); }}
                  style={{ flex: 1, padding: '7px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Metadata section */}
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', overflowY: 'auto', flex: isAddingTag ? 0 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', color: '#111827' }}>Details</h4>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  style={{ padding: '3px 10px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                >
                  Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={handleSaveMetadata} disabled={saving} style={{ padding: '3px 10px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                    {saving ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setIsEditing(false)} style={{ padding: '3px 10px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div><label style={labelStyle}>Title</label><input type="text" value={editForm.title} onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Description</label><textarea value={editForm.description} onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }} rows={2} /></div>
                <div><label style={labelStyle}>Category</label><input type="text" value={editForm.category} onChange={(e) => setEditForm(p => ({ ...p, category: e.target.value }))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Location</label><input type="text" value={editForm.location} onChange={(e) => setEditForm(p => ({ ...p, location: e.target.value }))} style={inputStyle} /></div>
                <div><label style={labelStyle}>Date Taken</label><input type="date" value={editForm.date_taken} onChange={(e) => setEditForm(p => ({ ...p, date_taken: e.target.value }))} style={inputStyle} /></div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px' }}>
                <MetaRow label="Title" value={photo.title || photo.file_name || 'Untitled'} />
                {photo.description && <MetaRow label="Description" value={photo.description} />}
                <MetaRow label="Category" value={photo.category || '—'} />
                <MetaRow label="Location" value={photo.location || '—'} />
                <MetaRow label="Date Taken" value={formatDate(photo.date_taken) || '—'} />
                <MetaRow label="Date Added" value={formatDateTime(photo.created_at)} />
                <MetaRow label="Uploaded By" value={photo.uploaded_by_name || '—'} />
                <MetaRow label="File" value={photo.file_name || photo.file_path || '—'} />
                <MetaRow label="Size" value={formatFileSize(photo.file_size)} />
                <MetaRow label="Type" value={photo.file_type || '—'} />
              </div>
            )}
          </div>

          {/* References section */}
          {!isAddingTag && (
            <div style={{ padding: '16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', color: '#111827' }}>
                  References ({tags.length})
                </h4>
                {!isAddingRef && (
                  <button
                    onClick={() => { setIsAddingRef(true); setRefForm({ linked_item_type: 'RFI', linked_item_id: '', description: '' }); }}
                    style={{ padding: '3px 10px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                  >
                    + Add
                  </button>
                )}
              </div>

              {/* Inline add reference form */}
              {isAddingRef && (
                <div style={{ padding: '10px', marginBottom: '10px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <label style={labelStyle}>Type</label>
                    <select value={refForm.linked_item_type} onChange={(e) => setRefForm(p => ({ ...p, linked_item_type: e.target.value }))} style={inputStyle}>
                      {TAG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <label style={labelStyle}>ID / Number</label>
                    <input type="text" value={refForm.linked_item_id} onChange={(e) => setRefForm(p => ({ ...p, linked_item_id: e.target.value }))} placeholder="e.g. RFI-001" style={inputStyle} autoFocus />
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <label style={labelStyle}>Note (optional)</label>
                    <input type="text" value={refForm.description} onChange={(e) => setRefForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief note" style={inputStyle} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={handleSaveRef}
                      disabled={refSaving || (!refForm.linked_item_id && !refForm.description)}
                      style={{ flex: 1, padding: '6px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', opacity: (refSaving || (!refForm.linked_item_id && !refForm.description)) ? 0.6 : 1 }}
                    >
                      {refSaving ? '...' : 'Add'}
                    </button>
                    <button onClick={() => setIsAddingRef(false)} style={{ flex: 1, padding: '6px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {tags.length === 0 && !isAddingRef ? (
                <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>
                  No references yet. Click "+ Add" to link to RFIs, ISO sheets, or other items. You can also click on the image to pin a reference to a specific area.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {tags.map((tag, idx) => {
                    const isSpatial = tag.x_coordinate >= 0 && tag.y_coordinate >= 0;
                    return (
                      <div
                        key={tag.id}
                        style={{
                          padding: '8px 10px', borderRadius: '6px',
                          backgroundColor: hoveredTag === tag.id ? '#eff6ff' : '#f9fafb',
                          border: '1px solid #e5e7eb', transition: 'background 0.15s',
                        }}
                        onMouseEnter={() => setHoveredTag(tag.id)}
                        onMouseLeave={() => setHoveredTag(null)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isSpatial ? (
                              <span style={{
                                width: 20, height: 20, borderRadius: '50%',
                                backgroundColor: hoveredTag === tag.id ? '#2563eb' : '#ef4444',
                                color: 'white', fontSize: '10px', fontWeight: 'bold',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>
                                {tags.filter(t => t.x_coordinate >= 0 && t.y_coordinate >= 0).indexOf(tag) + 1}
                              </span>
                            ) : (
                              <span style={{
                                width: 20, height: 20, borderRadius: '4px',
                                backgroundColor: '#2563eb',
                                color: 'white', fontSize: '10px', fontWeight: 'bold',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>
                                &#128279;
                              </span>
                            )}
                            <div>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{tag.linked_item_type}</span>
                              {tag.linked_item_id && <span style={{ fontSize: '12px', color: '#6b7280' }}> : {tag.linked_item_id}</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteTag(tag.id)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '14px', padding: '0 4px', opacity: 0.6 }}
                            title="Remove reference"
                          >
                            &times;
                          </button>
                        </div>
                        {tag.description && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginLeft: '26px' }}>{tag.description}</div>
                        )}
                        {isSpatial && (
                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px', marginLeft: '26px' }}>Pinned on image</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MetaRow = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '1px' }}>{label}</div>
    <div style={{ color: '#374151', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

export default PhotoDetailModal;
